// src/components/GreetingCard/ViewCardPage.jsx

import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getCardDetails, getPublicCard } from "../../utils/api";
import PreviewEnvelope from "./PreviewEnvelope";
import Button from "../ui/Button";
import { CARD_W, CARD_H } from "../../utils/geometry";
import { ArrowLeft, ChevronLeft, ChevronRight, Heart, Smartphone } from "lucide-react";

// Helper to parse width/height from card_size string like "800x600"
const parseWidth = (s) => {
  const m = /^(\d+)x\d+$/i.exec(s || "");
  return m ? +m[1] : null;
};

const parseHeight = (s) => {
  const m = /^\d+x(\d+)$/i.exec(s || "");
  return m ? +m[1] : null;
};

// Helper to detect video files by extension
const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.m4v'];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some(ext => lowerUrl.includes(ext));
};

export default function ViewCardPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [opened, setOpened] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [scale, setScale] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  
  // Track actual image dimensions for proper aspect ratio
  const [imageDimensions, setImageDimensions] = useState({ width: CARD_W, height: CARD_H });
  const dimensionsRestoredRef = useRef(false);

  // Mobile detection
  useEffect(() => {
    const checkIfMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  useEffect(() => {
    // Try authenticated endpoint first (for card owners)
    // Fall back to public endpoint (for recipients viewing sent cards)
    const loadCard = async () => {
      let authErr = null;
      let publicErr = null;

      // Try authenticated endpoint first
      try {
        const { data } = await getCardDetails(id);
        console.log("[ViewCardPage] Loaded via authenticated endpoint");
        setCard(data);
        restoreDimensions(data);
        setLoading(false);
        return; // Success, exit early
      } catch (err) {
        authErr = err;
        console.log("[ViewCardPage] Auth endpoint failed:", err.response?.status, err.response?.data);
      }

      // Fall back to public endpoint
      try {
        const { data } = await getPublicCard(id);
        console.log("[ViewCardPage] Loaded via public endpoint");
        setCard(data);
        restoreDimensions(data);
        setLoading(false);
        return; // Success, exit early
      } catch (err) {
        publicErr = err;
        console.log("[ViewCardPage] Public endpoint failed:", err.response?.status, err.response?.data);
      }

      // Both failed - determine the best error message
      setLoading(false);
      
      if (publicErr?.response?.status === 403) {
        // Card exists but is not publicly available (still a draft)
        setError("This card is not yet available for viewing. It may still be a draft - please log in or wait until it's been sent.");
      } else if (authErr?.response?.status === 401) {
        // User not logged in and card not found publicly
        setError("Please log in to view this card, or use the link sent to recipients.");
      } else if (publicErr?.response?.status === 404 || authErr?.response?.status === 404) {
        setError("Card not found. Please check the link and try again.");
      } else {
        setError("Could not load card. Please check the link and try again.");
      }
    };

    const restoreDimensions = (data) => {
      if (data.card_size) {
        const savedWidth = parseWidth(data.card_size);
        const savedHeight = parseHeight(data.card_size);
        if (savedWidth && savedHeight) {
          setImageDimensions({ width: savedWidth, height: savedHeight });
          dimensionsRestoredRef.current = true;
        }
      }
    };
    
    loadCard();
  }, [id]);

  // Track actual image dimensions if card_size wasn't saved (fallback)
  useEffect(() => {
    if (!card?.image_url || isVideoUrl(card?.image_url)) return;
    
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (dimensionsRestoredRef.current) return;
      setImageDimensions({ width: img.width, height: img.height });
    };
    img.src = card.image_url;
  }, [card?.image_url]);

  // Calculate dynamic dimensions
  const cardWidth = imageDimensions.width;
  const cardHeight = imageDimensions.height;

  // Responsive scaling
  useEffect(() => {
    const updateScale = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const isMobileView = viewportWidth < 768;
      
      // Reduced reserved space for header/controls to allow larger card
      const availableHeight = viewportHeight - 280;
      const availableWidth = viewportWidth - 60;
      
      let targetWidth, targetHeight;
      
      if (isMobileView) {
        targetWidth = availableWidth * 0.98;
        targetHeight = availableHeight * 0.85;
      } else {
        // Increased from 0.6/800/700 to 0.75/1000/850 for bigger cards
        targetWidth = Math.min(availableWidth * 0.75, 1000);
        targetHeight = Math.min(availableHeight * 0.9, 850);
      }
      
      const scaleX = targetWidth / cardWidth;
      const scaleY = targetHeight / cardHeight;
      let finalScale = Math.min(scaleX, scaleY);
      
      // Increased minimum scale for better visibility
      finalScale = isMobileView ? Math.max(finalScale, 0.35) : Math.max(finalScale, 0.5);
      setScale(finalScale);
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [cardWidth, cardHeight]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-sky-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4" />
          <p className="text-lg text-gray-600">Loading your card...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-pink-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-2xl shadow-lg max-w-md">
          <div className="text-red-500 text-5xl mb-4">📮</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Card Not Found</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  if (!card) return null;

  const {
    image_url,
    video_url,
    media_type = "image",
    front_text,
    inside_text,
    inside_text_entries = [],
    signatures = [],
    layout = {},
    inside_layout = {},
    inside_style = {},
    sender_name,
    category,
  } = card;

  // Determine if this is a video card
  const isVideo = media_type === "video" || !!video_url || isVideoUrl(image_url);
  const actualVideoUrl = video_url || (isVideoUrl(image_url) ? image_url : null);
  const actualImageUrl = isVideo ? null : image_url;

  // Convert inside_text to pages (support multi-page)
  const insidePages = inside_text ? inside_text.split('\n').map(t => t.trim()).filter(Boolean) : [""];

  // Text props from layout
  const textProps = {
    x: typeof layout.x === 'number' ? layout.x : cardWidth * 0.1,
    y: typeof layout.y === 'number' ? layout.y : cardHeight * 0.1,
    width: typeof layout.width === 'number' ? layout.width : cardWidth * 0.8,
    height: typeof layout.height === 'number' ? layout.height : cardHeight * 0.6,
    align: layout.align || 'center',
    text: front_text || layout.text || "",
    fontSize: layout.fontSize || 48,
    fontFamily: layout.fontFamily || 'Lilita One',
    fontWeight: layout.fontWeight || 700,
    lineHeight: layout.lineHeight || 1.2,
    letterSpacing: layout.letterSpacing || 1,
    fill: layout.fill || '#000000',
    stroke: layout.stroke || '',
    strokeWidth: layout.strokeWidth || 0,
    rotation: layout.rotation || 0,
    scaleX: layout.scaleX || 1,
    scaleY: layout.scaleY || 1,
    animStyle: layout.animStyle || 'none',
    transform: layout.transform || 'none',
    draggable: false,
  };

  // Inside text styling
  const insideFont = inside_style.fontFamily || "'Inter', sans-serif";
  const insideFontSize = inside_style.fontSize || 20;
  const insideFill = inside_style.fill || '#1f2937';
  const insideBorderColor = inside_style.borderColor || "#d4af37";
  const insideBorderWidth = inside_style.borderWidth ?? 9;
  const insideBorderStyle = inside_style.borderStyle || "solid";

  // Cover border/overlay styling
  const coverBorderColor = layout.borderColor || "";
  const coverBorderWidth = layout.borderWidth || 0;
  const coverBorderStyle = layout.borderStyle || "solid";
  const coverOverlay = layout.overlay || "none";
  const coverOverlayColor = layout.overlayColor || "#1F2937";

  const nextPage = () => {
    if (currentPage < insidePages.length - 1) {
      setCurrentPage(p => p + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(p => p - 1);
    } else {
      setFlipped(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-sky-50 to-pink-50 overflow-x-hidden">
      {/* Back button */}
      <div className="absolute top-4 left-4 z-10">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors bg-white/80 backdrop-blur-sm px-3 py-2 rounded-full shadow"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>
      </div>

      {/* Mobile Warning */}
      {isMobile && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mx-4 mt-4 rounded-r-lg shadow-sm">
          <div className="flex items-center">
            <Smartphone className="text-amber-600 w-5 h-5 mr-3" />
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-1">📱 Better on Desktop</p>
              <p>For the best experience, view on a larger screen.</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center pt-8 pb-4 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-center mb-4">
            <Heart className="text-red-500 w-6 h-6 mr-2" />
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              {category || 'Greeting'} Card
            </h1>
            <Heart className="text-red-500 w-6 h-6 ml-2" />
          </div>
          {sender_name && (
            <p className="text-lg text-gray-600 mb-2">
              From: <span className="font-semibold text-gray-900">{sender_name}</span>
            </p>
          )}
          {!opened && (
            <p className="text-sm text-gray-500">
              Click "Open Envelope" to reveal your card
            </p>
          )}
        </div>
      </div>

      {/* Card Container */}
      <div className="flex flex-col items-center justify-center px-2 md:px-4 w-full">
        <div
          style={{
            width: cardWidth * scale,
            height: cardHeight * scale,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            marginBottom: '1rem',
          }}
        >
          <div
            style={{
              transform: `scale(${scale})`,
              transformOrigin: 'top center',
              width: cardWidth,
              height: cardHeight,
            }}
          >
            <PreviewEnvelope
              opened={opened}
              flipped={flipped}
              imageUrl={actualImageUrl}
              videoUrl={actualVideoUrl}
              mediaType={isVideo ? "video" : "image"}
              text={textProps}
              setText={() => {}}
              inside={insidePages[currentPage] || ""}
              setInside={() => {}}
              insideFont={insideFont}
              insideFontSize={insideFontSize}
              insideFill={insideFill}
              insideBorderColor={insideBorderColor}
              insideBorderWidth={insideBorderWidth}
              insideBorderStyle={insideBorderStyle}
              coverBorderColor={coverBorderColor}
              coverBorderWidth={coverBorderWidth}
              coverBorderStyle={coverBorderStyle}
              coverOverlay={coverOverlay}
              coverOverlayColor={coverOverlayColor}
              insideLayout={inside_layout}
              signatures={signatures}
              cardWidth={cardWidth}
              cardHeight={cardHeight}
              insideTextEntries={inside_text_entries}
            />
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap justify-center gap-4 mt-4 mb-6">
          <Button 
            onClick={() => setOpened(o => !o)}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-full shadow-lg transform hover:scale-105 transition-all"
          >
            {opened ? 'Close Envelope' : 'Open Envelope'}
          </Button>
          
          <Button 
            onClick={() => setFlipped(f => !f)} 
            disabled={!opened}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold px-6 py-3 rounded-full shadow-lg transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {flipped ? 'Show Front' : 'Show Inside'}
          </Button>
        </div>

        {/* Page navigation */}
        {opened && flipped && insidePages.length > 1 && (
          <div className="flex items-center justify-center gap-4 mb-6">
            <Button
              onClick={prevPage}
              variant="secondary"
              className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-4 py-2 rounded-full shadow"
            >
              <ChevronLeft size={16} /> 
              <span>Previous</span>
            </Button>
            <span className="text-sm text-gray-600 min-w-[80px] text-center bg-white px-3 py-2 rounded-full shadow border">
              Page {currentPage + 1} of {insidePages.length}
            </span>
            <Button
              onClick={nextPage}
              disabled={currentPage === insidePages.length - 1}
              variant="secondary"
              className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-4 py-2 rounded-full shadow disabled:opacity-50"
            >
              <span>Next</span>
              <ChevronRight size={16} />
            </Button>
          </div>
        )}

        {/* Footer */}
        <div className="text-center pb-8 px-4">
          <div className="max-w-md mx-auto bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-white/20">
            <p className="text-sm text-gray-600 mb-2">
              💕 Created with love using
            </p>
            <p className="text-sm font-semibold text-gray-900">
              DreamableAI
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
